import {
  Button,
  Separator,
  Input,
  RadioGroup,
  RadioGroupItem,
  showConfirmationPrompt,
  ColoredAlert,
} from '@bpinternal/ui-kit-next'
import { getUseConfigStore } from '../../stores/useConfigStore'
import { SubSection } from './SubSection'
import { Plus, X } from 'lucide-react'
import { cn } from '~/utils/cn'
import React, { useEffect, useState } from 'react'
import { CodeSnippet, Paywall } from '~/components'
import { useSuspenseQuery } from '~/services'
import { config } from '~/shared'
import { useFeature } from '~/hooks'

export function AdvancedSettings({ workspaceId, botId }: { workspaceId: string; botId: string }) {
  const adminSecretEnabled = useFeature('webchat-admin-key')
  const useConfigStore = getUseConfigStore({ botId, workspaceId })

  const webhookId = useConfigStore((state) => state.webhookId)
  const allowedOrigins = useConfigStore((state) => state.allowedOrigins)
  const adminSecret = useConfigStore((state) => state.adminSecret)
  const [allowedOriginsMode, setAllowedOriginsMode] = useState<'all' | 'whitelist'>(
    (allowedOrigins?.length ?? 0) > 0 ? 'whitelist' : 'all'
  )

  const setAdminSecret = useConfigStore((state) => state.setAdminSecret)
  const setAllowedOrigins = useConfigStore((state) => state.setAllowedOrigins)

  function handleRemoveOrigin(index: number) {
    if (allowedOrigins?.[index] === '') {
      const newOrigins = [...(allowedOrigins ?? [])]
      setAllowedOrigins(newOrigins.filter((_, i) => i !== index))
      return
    }

    showConfirmationPrompt(
      `If you remove ${allowedOrigins?.[index]} from the list of allowed origins, you will no longer be able to access webchat from the Dashboard.`,
      {
        title: 'Are you sure?',
        variant: 'danger',
        confirmLabel: 'Remove',
        size: 'max-w-lg',
      }
    )
      .then(() => {
        const newOrigins = [...(allowedOrigins ?? [])]
        setAllowedOrigins(newOrigins.filter((_, i) => i !== index))
      })
      .catch(() => {
        // catch silently cancellation of the prompt
      })
  }
  return (
    <>
      {webhookId && (
        <SubSection
          title="Client ID"
          description={
            <>
              The client ID is used to identify the webchat instance. You&apos;ll find it most useful when building with
              our react library or if you&apos;re building a custom frontend.{' '}
              <a
                href="https://www.botpress.com/docs/webchat/react-library/get-started#obtain-your-client-id"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Learn more
              </a>
            </>
          }
          className="!flex flex-col pt-10"
        >
          <div className="!w-full md:!flex-col">
            <CodeSnippet className="text-muted-foreground" code={webhookId} />
          </div>
        </SubSection>
      )}
      {adminSecretEnabled && (
        <SubSection
          title="Admin Secret"
          description="Keep this secret secure. Admin secret to control user key generation."
          className="!flex flex-col"
        >
          <Input
            className="mt-1"
            placeholder="Enter admin secret"
            value={adminSecret}
            onChange={(e) => (e.target.value === '' ? setAdminSecret(undefined) : setAdminSecret(e.target.value))}
          />
        </SubSection>
      )}
      <Separator className="my-10" />
      <Paywall minimumPlan="team" analyticsEventName="allowed-origins">
        <SubSection
          title="Allowed Origins"
          description={
            <>
              Manage which origins are allowed to access the webchat.{' '}
              <a
                href="https://www.botpress.com/docs/learn/guides/advanced/safety/preventing-abuse#allowed-origins"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Learn more
              </a>
            </>
          }
        >
          <RadioGroup
            value={allowedOriginsMode === 'all' ? 'all' : 'whitelist'}
            onValueChange={(value) => {
              setAllowedOriginsMode(value as 'all' | 'whitelist')
              if (value === 'all') {
                setAllowedOrigins([])
              } else {
                setAllowedOrigins([config.dashboardUrl, config.cdnBaseUrl])
              }
            }}
            className="flex w-full justify-end gap-2"
          >
            <OriginsRadioGroupItem
              value="all"
              id="all-origins"
              label="All Origins"
              isSelected={allowedOriginsMode === 'all'}
            />
            <OriginsRadioGroupItem
              value="whitelist"
              id="whitelist"
              label="Whitelist"
              isSelected={allowedOriginsMode === 'whitelist'}
            />
          </RadioGroup>
        </SubSection>
        <AllowedOriginsSection
          workspaceId={workspaceId}
          allowedOrigins={allowedOrigins}
          allowedOriginsMode={allowedOriginsMode}
          setAllowedOrigins={setAllowedOrigins}
          handleRemoveOrigin={handleRemoveOrigin}
        />
      </Paywall>
    </>
  )
}

function OriginsRadioGroupItem({
  value,
  id,
  label,
  isSelected,
  className,
}: {
  value: string
  id: string
  label: string
  isSelected: boolean
  className?: string
}) {
  return (
    <div className={cn('flex items-center', className)}>
      <RadioGroupItem value={value} id={id} className="sr-only" />
      <label
        htmlFor={id}
        className={cn(
          'flex cursor-pointer items-center space-x-2 rounded-lg border px-4 py-2 transition-colors',
          isSelected
            ? 'border-gray-300 bg-gray-100 text-gray-900 dark:border-gray-700 dark:bg-neutral-200 dark:text-gray-900'
            : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50 dark:border-neutral-200 dark:bg-neutral-50 dark:text-gray-900 dark:hover:bg-neutral-100'
        )}
      >
        <div
          className={cn(
            'flex h-4 w-4 items-center justify-center rounded-full border-2',
            isSelected ? 'border-gray-900 bg-gray-900 dark:border-gray-50 dark:bg-gray-900' : ''
          )}
        >
          {isSelected && <div className="h-2 w-2 rounded-full bg-white dark:bg-gray-100" />}
        </div>
        <span className="text-sm font-medium">{label}</span>
      </label>
    </div>
  )
}

function AllowedOriginsSection({
  workspaceId,
  allowedOrigins,
  allowedOriginsMode,
  setAllowedOrigins,
  handleRemoveOrigin,
}: {
  workspaceId: string
  allowedOrigins: string[] | undefined
  allowedOriginsMode: 'all' | 'whitelist'
  setAllowedOrigins: (origins: string[]) => void
  handleRemoveOrigin: (index: number) => void
}) {
  const { plan } = useSuspenseQuery('workspaces_/$workspaceId_', { workspaceId }).data
  return (
    <div className="mb-4 mt-6 flex flex-col gap-4">
      {allowedOriginsMode === 'whitelist' &&
        (() => {
          const hasDashboardUrl = allowedOrigins?.includes(config.dashboardUrl) ?? false
          const hasCdnBaseUrl = allowedOrigins?.includes(config.cdnBaseUrl) ?? false
          const missingUrls = []
          if (!hasDashboardUrl) missingUrls.push(config.dashboardUrl)
          if (!hasCdnBaseUrl) missingUrls.push(config.cdnBaseUrl)

          if (missingUrls.length === 0) return null

          return (
            <ColoredAlert color="amber" className="mb-2">
              Webchat is currently inaccessible from the Dashboard. Add{' '}
              {missingUrls.map((url, index) => (
                <React.Fragment key={url}>
                  {index > 0 && (index === missingUrls.length - 1 ? ' and ' : ', ')}
                  <b>{url}</b>
                </React.Fragment>
              ))}{' '}
              to the allowlist to resolve this issue.
            </ColoredAlert>
          )
        })()}
      {plan !== 'community' &&
        allowedOrigins?.map((origin: string, index: number) => (
          <UrlInput
            key={index}
            index={index}
            initialValue={origin}
            setAllowedOrigins={setAllowedOrigins}
            allowedOrigins={allowedOrigins}
            handleRemoveOrigin={handleRemoveOrigin}
          />
        ))}
      <div className="flex w-full justify-end">
        {plan !== 'community' && plan !== 'plus' && allowedOriginsMode === 'whitelist' && (
          <Button
            variant="secondary"
            className="bg-neutral-50"
            onClick={() => setAllowedOrigins([...(allowedOrigins ?? []), ''])}
          >
            <Plus className="h-4 w-4" />
            Add Origin
          </Button>
        )}
      </div>
    </div>
  )
}

function UrlInput({
  index,
  initialValue,
  setAllowedOrigins,
  allowedOrigins,
  handleRemoveOrigin,
}: {
  index: number
  initialValue: string
  setAllowedOrigins: (origins: string[]) => void
  allowedOrigins: string[] | undefined
  handleRemoveOrigin: (index: number) => void
}) {
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  function handleChange(newValue: string) {
    setValue(newValue)
    const newOrigins = [...(allowedOrigins ?? [])]
    newOrigins[index] = newValue
    setAllowedOrigins(newOrigins)
  }

  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={'http://example.com'}
        className={cn(
          'pr-8',
          Boolean(value && value.trim() !== '') &&
            'border-gray-300 bg-gray-50 focus:border-gray-400 focus:ring-gray-200 dark:border-neutral-400 dark:bg-neutral-200 dark:focus:border-neutral-500 dark:focus:ring-gray-600'
        )}
      />
      {value && (
        <button
          type="button"
          onClick={() => handleRemoveOrigin(index)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:text-gray-700 focus:outline-none dark:text-neutral-500 dark:hover:text-neutral-600 dark:focus:text-gray-950"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}
